<template>

    <main class="_article pt-8 pb-8">
        <article class="prose p-3 max-w-7xl m-auto">
            <div class="mb-2 text-sm lg:px-44 flex items-center justify-between">
                <NuxtLink href="/blog" class="text-sm py-1 px-2 no-underline btn btn-sm btn-gray flex items-center">
                    <Icon name="material-symbols:arrow-back" class="mr-1" />
                    All articles
                </NuxtLink>
                <Share v-if="article" class="inline-block ml-2" :title="article?.title"
                    :summary="article?.description" />
                <Feedback class="inline-block" />
            </div>

            <div v-if="publishedDate" class="mt-4 lg:px-44 text-sm text-gray-400">
                <time :datetime="publishedDate">Published {{ formattedDate }}</time>
                <template v-if="showUpdatedDate">
                    &middot; <time :datetime="updatedDate">Last updated {{ formattedUpdatedDate }}</time>
                </template>
            </div>

            <ContentRenderer v-if="article" class="content-doc mt-8 lg:px-44" :value="article" />

            <div class="mb-2 mt-8 text-sm lg:px-44 flex items-center justify-between">
                <NuxtLink href="/blog" class="text-sm py-1 px-2 no-underline btn btn-sm btn-gray flex items-center"
                    style="text-decoration:none">
                    <Icon name="material-symbols:arrow-back" class="mr-1" />
                    All articles
                </NuxtLink>
                <Share v-if="article" class="inline-block ml-2" :title="article?.title"
                    :summary="article?.description" />
                <Feedback class="inline-block" />
            </div>
        </article>
    </main>
</template>

<script lang="ts" setup>
const route = useRoute();

definePageMeta({
    alias: ['/help/:slug'],
});

// Path must be unique per URL: a static useAsyncData key dedupes across all blog posts and
// SSR/prerender can serve the wrong article for every slug.
const contentPath = route.path.replace(/^\/help\//, '/blog/');
const { data: article } = await useAsyncData(
    `article-meta:${contentPath}`,
    () => queryCollection('content').path(contentPath).first()
);

// Explicit metaTitle/metaDescription (from Notion, once the admin articles API
// surfaces them — see helpcenter.ts) win when present; otherwise fall back to
// the derived title/description, tightened to SERP-safe lengths. Never emit an
// untrimmed H1-length title or an over-length description (TICKET-10023).
const seoTitle = computed(() => {
    const raw = article.value?.metaTitle || article.value?.title;
    return raw ? tightenSeoTitle(raw, ' | Unified.to') : 'Blog | Unified.to';
});
const seoDescription = computed(() => {
    const raw = article.value?.metaDescription || article.value?.description;
    return raw ? trimSeoDescription(raw) : undefined;
});

// Compute canonical URL and absolute image URL for OG/Twitter tags
const siteBaseUrl = 'https://unified.to';
// Prefer contentPath (maps /help/:slug → /blog/:slug) so aliases share one canonical.
// Use path only (not fullPath) so query/hash never leak — same trim as useSEO.ts.
const canonicalUrl = computed(() => {
    const path = contentPath === '/' ? '/' : contentPath.replace(/\/+$/, '');
    return `${siteBaseUrl}${path}`;
});
const ogImageUrl = computed(() => {
    const src = article.value?.img || '/logos/Unified_Black.png';
    return /^https?:\/\//i.test(src) ? src : new URL(src, siteBaseUrl).toString();
});
// Per-article image dimensions aren't known ahead of time, so only publish
// og:image:type (inferred from the file extension) and og:image:alt — never
// og:image:width/height, which would misreport the actual image size.
const ogImageType = computed(() => {
    const ext = ogImageUrl.value.split('.').pop()?.toLowerCase().split(/[?#]/)[0];
    const types: Record<string, string> = {
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        webp: 'image/webp',
        gif: 'image/gif',
        svg: 'image/svg+xml',
    };
    return ext ? types[ext] : undefined;
});
const publishedDate = computed(() => {
    if (!article.value?.date) {
        return undefined;
    }

    const parsedDate = new Date(article.value.date);
    if (Number.isNaN(parsedDate.getTime())) {
        return undefined;
    }

    return parsedDate.toISOString().split('T')[0];
});
const formattedDate = computed(() => {
    if (!publishedDate.value) return '';
    const d = new Date(publishedDate.value + 'T00:00:00');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
});
const updatedDate = computed(() => {
    if (!article.value?.updated) {
        return undefined;
    }

    const parsedDate = new Date(article.value.updated);
    if (Number.isNaN(parsedDate.getTime())) {
        return undefined;
    }

    return parsedDate.toISOString().split('T')[0];
});
const formattedUpdatedDate = computed(() => {
    if (!updatedDate.value) return '';
    const d = new Date(updatedDate.value + 'T00:00:00');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
});
// Only surface "Last updated" when it's meaningfully after the original publish date.
const showUpdatedDate = computed(() => !!updatedDate.value && !!publishedDate.value && updatedDate.value > publishedDate.value);
const articleJsonLd = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl.value,
    },
    url: canonicalUrl.value,
    headline: article.value?.title,
    description: article.value?.description,
    datePublished: publishedDate.value,
    dateModified: updatedDate.value || publishedDate.value,
    image: ogImageUrl.value,
    author: {
        '@type': 'Organization',
        name: 'Unified.to',
        url: 'https://unified.to',
    },
    publisher: {
        '@type': 'Organization',
        name: 'Unified.to',
        url: 'https://unified.to',
        logo: {
            '@type': 'ImageObject',
            url: 'https://unified.to/logos/Unified_Black.png',
        },
    },
}));

// Set full Open Graph and Twitter metadata per article
useHead(() => ({
    title: seoTitle.value,
    link: [
        { rel: 'canonical', href: canonicalUrl.value },
    ],
    meta: [
        { name: 'description', content: seoDescription.value },
        { property: 'og:title', content: seoTitle.value },
        { property: 'og:description', content: seoDescription.value },
        { property: 'og:image', content: ogImageUrl.value },
        ...(ogImageType.value ? [{ property: 'og:image:type', content: ogImageType.value }] : []),
        { property: 'og:image:alt', content: article.value?.title || 'Unified.to' },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: canonicalUrl.value },
        { property: 'article:published_time', content: publishedDate.value },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: seoTitle.value },
        { name: 'twitter:description', content: seoDescription.value },
        { name: 'twitter:image', content: ogImageUrl.value },
    ],
    script: [{
        type: 'application/ld+json',
        key: 'article-structured-data',
        textContent: JSON.stringify(articleJsonLd.value),
    }],
}));
</script>

<style></style>